import React, { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { LoadFailed } from "@/components/LoadState";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/accountRole";
import { Crosshair } from "@/components/Crosshair";
import { timeAgo } from "@/lib/constants";
import {
  MessageSquarePlus, Bug, Lightbulb, HelpCircle, MoreHorizontal, UserCheck,
  PlayCircle, CheckCircle2, Lock, History as HistoryIcon, PlusCircle, CircleDot, ChevronDown, ChevronRight,
  RotateCcw, CheckSquare, Square, AlertTriangle, X, GitMerge, Layers, Link2, Undo2,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { RoleTag } from "@/components/admin/RoleTag";

const TABS = ["open", "in_progress", "resolved", "all"];
const TAB_LABEL = { open: "Open", in_progress: "In Progress", resolved: "Resolved / Closed", all: "All" };
const CAT_ICON = { Bug, Idea: Lightbulb, Confusing: HelpCircle, Other: MoreHorizontal };
const STATUS_META = {
  open: { label: "Open", cls: "border-volt/50 text-volt" },
  in_progress: { label: "In Progress", cls: "border-yellow-500/50 text-yellow-400" },
  resolved: { label: "Resolved / Closed", cls: "border-green-500/50 text-green-400" },
};
const HIST_ICON = { created: PlusCircle, assignment: UserCheck, status: CircleDot, reopen: RotateCcw, merge: GitMerge };

export default function AdminFeedback() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("open");
  const [items, setItems] = useState([]);
  const [loadErr, setLoadErr] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [openHistory, setOpenHistory] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [modal, setModal] = useState(null); // { type: 'close' | 'reopen', id }
  const [mReason, setMReason] = useState("");
  const [mVerified, setMVerified] = useState(false);
  const [mWithout, setMWithout] = useState(false);

  // selection + bulk
  const [selected, setSelected] = useState(() => new Set());
  const [assignMenu, setAssignMenu] = useState(false);
  const [bulk, setBulk] = useState(null); // { type: 'start' | 'resolve' | 'merge' }
  const [bReason, setBReason] = useState("");
  const [bVerified, setBVerified] = useState(false);
  const [bWithout, setBWithout] = useState(false);
  const [primaryId, setPrimaryId] = useState(null);
  const lastIdx = useRef(null);

  const openModal = (type, id) => { setModal({ type, id }); setMReason(""); setMVerified(false); setMWithout(false); };
  const closeModal = () => setModal(null);
  const clearSelection = () => { setSelected(new Set()); lastIdx.current = null; };

  const load = (key) => {
    const qs = key === "all" ? "" : `?status=${key}`;
    setLoadErr(null);
    api.get(`/admin/beta/feedback${qs}`).then(({ data }) => setItems(data.feedback))
      .catch((err) => setLoadErr(formatError(err?.response?.data?.detail) || "Couldn't load feedback."));
  };

  useEffect(() => {
    if (user && isAdminRole(user.role)) {
      load(tab);
      clearSelection(); // selection is always scoped to the visible tab
      api.get("/admin/admins").then(({ data }) => setAdmins(data.admins || [])).catch(() => {});
    }
    // eslint-disable-next-line
  }, [user, tab]);

  if (loading || user === null) return <div className="min-h-screen" />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdminRole(user.role)) return <Navigate to="/feed" replace />;

  // ---- selection helpers (scoped to what THIS tab shows) ----
  const idxOf = (fid) => items.findIndex((f) => f.feedback_id === fid);
  const handleCheck = (fid, e) => {
    const idx = idxOf(fid);
    if (e.nativeEvent?.shiftKey && lastIdx.current != null) {
      const [a, b] = [lastIdx.current, idx].sort((x, y) => x - y);
      setSelected((s) => { const n = new Set(s); for (let i = a; i <= b; i++) n.add(items[i].feedback_id); return n; });
    } else {
      setSelected((s) => { const n = new Set(s); n.has(fid) ? n.delete(fid) : n.add(fid); return n; });
    }
    lastIdx.current = idx;
  };
  const allSelected = items.length > 0 && items.every((f) => selected.has(f.feedback_id));
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) items.forEach((f) => n.delete(f.feedback_id));
    else items.forEach((f) => n.add(f.feedback_id));
    return n;
  });

  const selItems = items.filter((f) => selected.has(f.feedback_id));
  const startSkip = selItems.filter((f) => f.status !== "open");
  const resolveSkip = selItems.filter((f) => f.status === "resolved");

  const assign = async (id, assigneeId) => {
    setBusyId(id);
    try {
      await api.post(`/admin/beta/feedback/${id}/assign`, { assignee_id: assigneeId || null });
      toast.success(assigneeId ? "Ticket assigned." : "Ticket unassigned.");
      load(tab);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const setStatus = async (id, status, extra = {}) => {
    setBusyId(id);
    try {
      await api.post(`/admin/beta/feedback/${id}/status`, { status, ...extra });
      const msg = status === "resolved" ? "Ticket resolved and closed."
        : status === "open" && extra.reason ? "Ticket reopened."
        : `Moved to ${STATUS_META[status]?.label || status}.`;
      toast.success(msg);
      closeModal();
      load(tab);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const submitClose = async () => {
    if (!mVerified && !mWithout) { toast.error("Confirm whether this was verified on production before closing."); return; }
    await setStatus(modal.id, "resolved", { reason: mReason.trim(), verified_on_production: mVerified, closing_without_verification: mWithout });
  };
  const submitReopen = async () => {
    if (mReason.trim().length < 5) { toast.error("A reason (at least 5 characters) is required to reopen a closed ticket."); return; }
    await setStatus(modal.id, "open", { reason: mReason.trim() });
  };

  // ---- bulk actions ----
  const runBulkAssign = async (assigneeId) => {
    setAssignMenu(false);
    try {
      const { data } = await api.post("/admin/beta/feedback/bulk-assign", { feedback_ids: [...selected], assignee_id: assigneeId || null });
      toast.success(`Assigned ${data.changed}${data.skipped ? ` · ${data.skipped} skipped` : ""}.`);
      clearSelection(); load(tab);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const openBulk = (type) => {
    if (type === "merge" && selected.size < 2) { toast.error("Select at least two tickets to merge."); return; }
    setBReason(""); setBVerified(false); setBWithout(false);
    if (type === "merge") setPrimaryId([...selItems].find((f) => !f.merged_into)?.feedback_id || selItems[0]?.feedback_id || null);
    setBulk({ type });
  };
  const runBulkStart = async () => {
    try {
      const { data } = await api.post("/admin/beta/feedback/bulk-status", { feedback_ids: [...selected], status: "in_progress" });
      toast.success(`Started ${data.changed}${data.skipped ? ` · ${data.skipped} skipped` : ""}.`);
      setBulk(null); clearSelection(); load(tab);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const runBulkResolve = async () => {
    if (!bVerified && !bWithout) { toast.error("Confirm whether these were verified on production before closing."); return; }
    try {
      const { data } = await api.post("/admin/beta/feedback/bulk-status", {
        feedback_ids: [...selected], status: "resolved", reason: bReason.trim(),
        verified_on_production: bVerified, closing_without_verification: bWithout,
      });
      toast.success(`Resolved ${data.changed}${data.skipped ? ` · ${data.skipped} skipped` : ""}.`);
      setBulk(null); clearSelection(); load(tab);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const runMerge = async () => {
    if (!primaryId) { toast.error("Pick a primary ticket."); return; }
    const dupes = [...selected].filter((id) => id !== primaryId);
    if (dupes.length === 0) { toast.error("Select at least one other ticket to fold into the primary."); return; }
    try {
      const { data } = await api.post("/admin/beta/feedback/merge", { primary_id: primaryId, duplicate_ids: dupes });
      toast.success(`Merged ${data.merged} duplicate(s) into the primary ticket.`);
      setBulk(null); clearSelection(); load(tab);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const unmerge = async (fid) => {
    setBusyId(fid);
    try {
      const { data } = await api.post(`/admin/beta/feedback/${fid}/unmerge`);
      toast.success(`Unmerged ${data.restored} ticket(s) — restored.`);
      load(tab);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  return (
    <div className="text-foreground">
      <div className="max-w-[1000px] mx-auto px-4 py-6 pb-28">
        <div className="flex items-center gap-2">
          <MessageSquarePlus className="w-6 h-6 text-volt" strokeWidth={1.6} />
          <h1 className="font-heading text-4xl font-bold uppercase tracking-tight" data-testid="feedback-admin-title">Beta Feedback</h1>
        </div>
        <p className="text-[11px] font-mono uppercase text-muted-foreground mt-1">Assign, advance, close, and merge duplicate tickets. Every change — single or bulk — is logged.</p>
        <Crosshair className="my-5" />

        <div className="inline-flex border border-sniper bg-surface p-1 mb-5" data-testid="feedback-tabs">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`fb-tab-${t}`}
              className={`px-4 py-2 font-mono uppercase text-xs tracking-wide transition-colors ${tab === t ? "bg-olive text-white" : "text-secondary-foreground hover:text-white"}`}>
              {TAB_LABEL[t]}
            </button>
          ))}
        </div>

        {/* toolbar: select-all (only what THIS tab shows) */}
        {items.length > 0 && (
          <div className="flex items-center gap-3 mb-3" data-testid="feedback-toolbar">
            <button onClick={toggleAll} data-testid="feedback-select-all"
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase text-secondary-foreground hover:text-foreground transition-colors">
              {allSelected ? <CheckSquare className="w-4 h-4 text-olive-bright" /> : <Square className="w-4 h-4" />}
              Select all ({items.length})
            </button>
            {selected.size > 0 && (
              <span className="font-mono text-[11px] uppercase text-muted-foreground" data-testid="feedback-selected-count">{selected.size} selected</span>
            )}
          </div>
        )}

        <div className="space-y-3" data-testid="feedback-list">
          {loadErr && <LoadFailed reason={loadErr} onRetry={() => load(tab)} testid="feedback-load-failed" />}
          {!loadErr && items.length === 0 && <p className="text-sm font-body text-muted-foreground border border-sniper bg-surface p-6" data-testid="feedback-empty">No {TAB_LABEL[tab].toLowerCase()} tickets.</p>}
          {!loadErr && items.map((f) => {
            const Icon = CAT_ICON[f.category] || MoreHorizontal;
            const sm = STATUS_META[f.status] || STATUS_META.open;
            const closed = f.status === "resolved";
            const historyOpen = !!openHistory[f.feedback_id];
            const busy = busyId === f.feedback_id;
            const isSelected = selected.has(f.feedback_id);
            const isPrimary = (f.merged_children || []).length > 0;
            const isDuplicate = !!f.merged_into;
            return (
              <div key={f.feedback_id} className={`border bg-surface p-4 transition-colors ${isSelected ? "border-olive-bright" : "border-sniper"}`} data-testid={`feedback-item-${f.feedback_id}`}>
                <div className="flex items-start gap-3">
                  <button onClick={(e) => handleCheck(f.feedback_id, e)} data-testid={`feedback-check-${f.feedback_id}`}
                    className="mt-0.5 shrink-0 text-secondary-foreground hover:text-foreground transition-colors" aria-label="select ticket">
                    {isSelected ? <CheckSquare className="w-5 h-5 text-olive-bright" /> : <Square className="w-5 h-5" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-wide border border-olive/50 text-olive-bright px-2 py-0.5">
                        <Icon className="w-3 h-3" /> {f.category}
                      </span>
                      <span className={`inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-wide border px-2 py-0.5 ${sm.cls}`} data-testid={`feedback-status-${f.feedback_id}`}>
                        {closed && <Lock className="w-3 h-3" />} {sm.label}
                      </span>
                      {isPrimary && (
                        <span className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-wide border border-olive-bright/60 text-olive-bright px-2 py-0.5" data-testid={`feedback-primary-${f.feedback_id}`}>
                          <Layers className="w-3 h-3" /> Primary · {f.merged_from?.length || f.merged_children.length} merged
                        </span>
                      )}
                      {isDuplicate && (
                        <span className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-wide border border-sniper text-muted-foreground px-2 py-0.5" data-testid={`feedback-duplicate-${f.feedback_id}`}>
                          <Link2 className="w-3 h-3" /> Duplicate → #{f.merged_into}
                        </span>
                      )}
                      {f.reopen_count > 0 && (
                        <span className={`inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-wide border px-2 py-0.5 ${f.reopened_flag ? "border-blaze/60 text-blaze" : "border-amber-400/50 text-amber-400"}`} data-testid={`feedback-reopened-${f.feedback_id}`}>
                          <RotateCcw className="w-3 h-3" /> Reopened ×{f.reopen_count}{f.reopened_flag ? " ⚠" : ""}
                        </span>
                      )}
                      {f.assigned_to_username && (
                        <span className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-wide border border-sniper text-secondary-foreground px-2 py-0.5">
                          <UserCheck className="w-3 h-3" /> {f.assigned_to_username}
                        </span>
                      )}
                      <span className="text-[11px] font-mono uppercase text-muted-foreground inline-flex items-center gap-2">{f.username || f.user_email || "anonymous"} {f.username && <RoleTag kind={f.submitter_kind} />} · {timeAgo(f.created_at)}</span>
                    </div>
                    <p className="text-sm font-body text-foreground mt-2 whitespace-pre-wrap break-words">{f.message}</p>
                    {f.url && <p className="text-[11px] font-mono text-muted-foreground mt-1.5 truncate">page: {f.url}</p>}
                    {f.user_agent && <p className="text-[11px] font-mono text-muted-foreground truncate">user agent: {f.user_agent}</p>}

                    {/* merged reporters / environments rolled up onto the primary */}
                    {isPrimary && (f.merged_from || []).length > 0 && (
                      <div className="mt-3 border border-sniper/70 bg-obsidian/40 p-3" data-testid={`feedback-merged-from-${f.feedback_id}`}>
                        <p className="font-mono text-[10px] uppercase text-olive-bright mb-2 flex items-center gap-1"><Layers className="w-3 h-3" /> Also reported by ({f.merged_from.length})</p>
                        <ul className="space-y-1.5">
                          {f.merged_from.map((r) => (
                            <li key={r.feedback_id} className="font-mono text-[10px] text-secondary-foreground">
                              <span className="text-foreground">{r.username || r.user_email || "anonymous"}</span> · {timeAgo(r.created_at)}
                              {r.url && <span className="text-muted-foreground"> · page: {r.url}</span>}
                              {r.user_agent && <div className="text-muted-foreground truncate">UA: {r.user_agent}</div>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* controls */}
                <div className="mt-3 pt-3 border-t border-sniper flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <label className="font-mono text-[10px] uppercase text-muted-foreground">Assign to</label>
                    <select
                      data-testid={`feedback-assign-${f.feedback_id}`}
                      value={f.assigned_to || ""}
                      disabled={closed || busy}
                      onChange={(e) => assign(f.feedback_id, e.target.value)}
                      className="bg-input border border-sniper focus:border-olive outline-none px-2 py-1.5 text-foreground font-mono text-xs disabled:opacity-50 transition-colors">
                      <option value="">Unassigned</option>
                      {admins.map((a) => <option key={a.user_id} value={a.user_id}>{a.username || a.email}</option>)}
                    </select>
                  </div>

                  {!closed ? (
                    <div className="flex items-center gap-2">
                      {f.status === "open" && (
                        <button onClick={() => setStatus(f.feedback_id, "in_progress")} disabled={busy} data-testid={`feedback-start-${f.feedback_id}`}
                          className="inline-flex items-center gap-1 border border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 font-mono uppercase text-xs px-3 py-1.5 transition-colors disabled:opacity-50">
                          <PlayCircle className="w-3.5 h-3.5" /> Start
                        </button>
                      )}
                      {f.status === "in_progress" && (
                        <button onClick={() => setStatus(f.feedback_id, "open")} disabled={busy} data-testid={`feedback-back-open-${f.feedback_id}`}
                          className="inline-flex items-center gap-1 border border-sniper text-secondary-foreground hover:text-foreground font-mono uppercase text-xs px-3 py-1.5 transition-colors disabled:opacity-50">
                          Back to open
                        </button>
                      )}
                      <button onClick={() => openModal("close", f.feedback_id)} disabled={busy} data-testid={`feedback-resolve-${f.feedback_id}`}
                        className="inline-flex items-center gap-1 border border-green-500/50 text-green-400 hover:bg-green-500/10 font-mono uppercase text-xs px-3 py-1.5 transition-colors disabled:opacity-50">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Resolve &amp; close
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1 font-mono text-[11px] uppercase text-muted-foreground"><Lock className="w-3.5 h-3.5" /> Closed — history retained</span>
                      {isPrimary ? (
                        <button onClick={() => unmerge(f.feedback_id)} disabled={busy} data-testid={`feedback-unmerge-${f.feedback_id}`}
                          className="inline-flex items-center gap-1 border border-amber-400/50 text-amber-400 hover:bg-amber-400/10 font-mono uppercase text-xs px-3 py-1.5 transition-colors disabled:opacity-50">
                          <Undo2 className="w-3.5 h-3.5" /> Unmerge
                        </button>
                      ) : (
                        <button onClick={() => openModal("reopen", f.feedback_id)} disabled={busy} data-testid={`feedback-reopen-closed-${f.feedback_id}`}
                          className="inline-flex items-center gap-1 border border-volt/50 text-volt hover:bg-volt/10 font-mono uppercase text-xs px-3 py-1.5 transition-colors disabled:opacity-50">
                          <RotateCcw className="w-3.5 h-3.5" /> Reopen
                        </button>
                      )}
                    </div>
                  )}

                  {isPrimary && !closed && (
                    <button onClick={() => unmerge(f.feedback_id)} disabled={busy} data-testid={`feedback-unmerge-open-${f.feedback_id}`}
                      className="inline-flex items-center gap-1 border border-amber-400/50 text-amber-400 hover:bg-amber-400/10 font-mono uppercase text-xs px-3 py-1.5 transition-colors disabled:opacity-50">
                      <Undo2 className="w-3.5 h-3.5" /> Unmerge
                    </button>
                  )}

                  <button onClick={() => setOpenHistory((p) => ({ ...p, [f.feedback_id]: !p[f.feedback_id] }))}
                    data-testid={`feedback-history-toggle-${f.feedback_id}`}
                    className="ml-auto inline-flex items-center gap-1 font-mono text-[11px] uppercase text-secondary-foreground hover:text-foreground transition-colors">
                    {historyOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    <HistoryIcon className="w-3.5 h-3.5" /> History ({(f.history || []).length})
                  </button>
                </div>

                {historyOpen && (
                  <ol className="mt-3 border-l border-sniper pl-4 space-y-2.5" data-testid={`feedback-history-${f.feedback_id}`}>
                    {(f.history || []).map((h, i) => {
                      const HIcon = HIST_ICON[h.type] || CircleDot;
                      return (
                        <li key={h.id || i} className="relative">
                          <span className="absolute -left-[21px] top-0.5 bg-surface"><HIcon className="w-3.5 h-3.5 text-olive-bright" /></span>
                          <p className="font-mono text-[11px] text-foreground">{h.detail}</p>
                          <p className="font-mono text-[10px] text-muted-foreground">{h.actor_username} · {timeAgo(h.at)}</p>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ---- BULK ACTION BAR ---- */}
      {selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 bg-black/95 border border-sniper px-4 py-3 shadow-lg flex-wrap max-w-[95vw]" data-testid="feedback-bulk-bar">
          <span className="font-mono text-xs uppercase text-foreground" data-testid="feedback-bulk-count">{selected.size} selected</span>
          <div className="relative">
            <button onClick={() => setAssignMenu((v) => !v)} data-testid="feedback-bulk-assign-btn"
              className="inline-flex items-center gap-1.5 border border-sniper text-secondary-foreground hover:text-foreground font-mono uppercase text-[10px] px-3 py-1.5 transition-colors">
              <UserCheck className="w-3 h-3" /> Assign to
            </button>
            {assignMenu && (
              <div className="absolute bottom-full mb-1 left-0 bg-surface border border-sniper min-w-[180px] max-h-64 overflow-y-auto" data-testid="feedback-bulk-assign-menu">
                <button onClick={() => runBulkAssign("")} data-testid="feedback-bulk-assign-none" className="w-full text-left px-3 py-2 font-mono uppercase text-[10px] text-muted-foreground hover:bg-white/5 hover:text-foreground">Unassign</button>
                {admins.map((a) => (
                  <button key={a.user_id} onClick={() => runBulkAssign(a.user_id)} data-testid={`feedback-bulk-assign-${a.user_id}`}
                    className="w-full text-left px-3 py-2 font-mono uppercase text-[10px] text-secondary-foreground hover:bg-white/5 hover:text-foreground">{a.username || a.email}</button>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => openBulk("start")} data-testid="feedback-bulk-start-btn"
            className="inline-flex items-center gap-1.5 border border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 font-mono uppercase text-[10px] px-3 py-1.5 transition-colors">
            <PlayCircle className="w-3 h-3" /> Start
          </button>
          <button onClick={() => openBulk("resolve")} data-testid="feedback-bulk-resolve-btn"
            className="inline-flex items-center gap-1.5 border border-green-500/50 text-green-400 hover:bg-green-500/10 font-mono uppercase text-[10px] px-3 py-1.5 transition-colors">
            <CheckCircle2 className="w-3 h-3" /> Resolve &amp; close
          </button>
          <button onClick={() => openBulk("merge")} data-testid="feedback-bulk-merge-btn"
            className="inline-flex items-center gap-1.5 border border-olive/60 text-olive-bright hover:bg-olive/10 font-mono uppercase text-[10px] px-3 py-1.5 transition-colors">
            <GitMerge className="w-3 h-3" /> Merge duplicates
          </button>
          <button onClick={clearSelection} data-testid="feedback-bulk-clear-btn" className="font-mono uppercase text-[10px] text-muted-foreground hover:text-foreground">Clear selection</button>
        </div>
      )}

      {/* ---- BULK START confirm ---- */}
      {bulk?.type === "start" && (
        <BulkShell title="Move to In Progress" icon={<PlayCircle className="w-5 h-5 text-yellow-400" />} onClose={() => setBulk(null)}>
          <p className="text-sm font-body text-secondary-foreground" data-testid="feedback-bulk-start-summary">
            Start <span className="text-foreground font-bold">{selItems.length - startSkip.length}</span> of {selItems.length} selected?
          </p>
          {startSkip.length > 0 && (
            <p className="text-[12px] font-mono text-amber-400 mt-2" data-testid="feedback-bulk-start-skip">
              {startSkip.length} will be skipped ({startSkip.filter((f) => f.status === "resolved").length} already closed, {startSkip.filter((f) => f.status === "in_progress").length} already in progress).
            </p>
          )}
          <div className="mt-4 flex items-center justify-end gap-2">
            <button onClick={() => setBulk(null)} data-testid="feedback-bulk-start-cancel" className="border border-sniper text-muted-foreground hover:text-foreground font-mono uppercase text-xs px-3 py-2 transition-colors">Cancel</button>
            <button onClick={runBulkStart} disabled={selItems.length - startSkip.length === 0} data-testid="feedback-bulk-start-confirm"
              className="font-mono uppercase text-xs px-4 py-2 border border-yellow-500/60 text-yellow-400 hover:bg-yellow-500/10 transition-colors disabled:opacity-50">Move to In Progress</button>
          </div>
        </BulkShell>
      )}

      {/* ---- BULK RESOLVE confirm (with verification) ---- */}
      {bulk?.type === "resolve" && (
        <BulkShell title="Resolve & close" icon={<CheckCircle2 className="w-5 h-5 text-green-400" />} onClose={() => setBulk(null)}>
          <p className="text-sm font-body text-secondary-foreground" data-testid="feedback-bulk-resolve-summary">
            Resolve <span className="text-foreground font-bold">{selItems.length - resolveSkip.length}</span> of {selItems.length} selected?
          </p>
          {resolveSkip.length > 0 && (
            <p className="text-[12px] font-mono text-amber-400 mt-2" data-testid="feedback-bulk-resolve-skip">
              {resolveSkip.length} {resolveSkip.length === 1 ? "is" : "are"} already closed and will be skipped.
            </p>
          )}
          <div className="mt-4 space-y-3">
            <p className="text-[12px] font-body text-secondary-foreground">Confirm the verification state before closing. This is recorded in each ticket's history.</p>
            <button type="button" onClick={() => { const nv = !bVerified; setBVerified(nv); if (nv) setBWithout(false); }} data-testid="feedback-bulk-verified-check"
              className={`w-full flex items-center gap-2 border px-3 py-2.5 font-mono text-xs uppercase transition-colors ${bVerified ? "border-green-500/60 text-green-400 bg-green-500/5" : "border-sniper text-secondary-foreground hover:text-foreground"}`}>
              {bVerified ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />} Verified on PRODUCTION
            </button>
            <button type="button" onClick={() => { const nw = !bWithout; setBWithout(nw); if (nw) setBVerified(false); }} data-testid="feedback-bulk-without-check"
              className={`w-full flex items-center gap-2 border px-3 py-2.5 font-mono text-xs uppercase transition-colors ${bWithout ? "border-amber-400/60 text-amber-400 bg-amber-400/5" : "border-sniper text-secondary-foreground hover:text-foreground"}`}>
              {bWithout ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />} Closing without verification
            </button>
            <textarea value={bReason} onChange={(e) => setBReason(e.target.value)} rows={2} placeholder="Optional note (applied to all)…" data-testid="feedback-bulk-reason"
              className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground text-sm resize-none" />
          </div>
          <div className="mt-4 flex items-center justify-end gap-2">
            <button onClick={() => setBulk(null)} data-testid="feedback-bulk-resolve-cancel" className="border border-sniper text-muted-foreground hover:text-foreground font-mono uppercase text-xs px-3 py-2 transition-colors">Cancel</button>
            <button onClick={runBulkResolve} disabled={selItems.length - resolveSkip.length === 0} data-testid="feedback-bulk-resolve-confirm"
              className="font-mono uppercase text-xs px-4 py-2 border border-green-500/60 text-green-400 hover:bg-green-500/10 transition-colors disabled:opacity-50">Resolve &amp; close</button>
          </div>
        </BulkShell>
      )}

      {/* ---- MERGE modal ---- */}
      {bulk?.type === "merge" && (
        <BulkShell title="Merge duplicates" icon={<GitMerge className="w-5 h-5 text-olive-bright" />} onClose={() => setBulk(null)} wide>
          <p className="text-[12px] font-body text-secondary-foreground">
            Pick the <span className="text-foreground font-bold">primary</span>. The others close as duplicates linked to it, and their reporters + environments roll up onto the primary. This is reversible — Unmerge restores them.
          </p>
          <div className="mt-3 space-y-2 max-h-[46vh] overflow-y-auto" data-testid="feedback-merge-list">
            {selItems.map((f) => {
              const isP = primaryId === f.feedback_id;
              const alreadyDup = !!f.merged_into;
              return (
                <button key={f.feedback_id} onClick={() => !alreadyDup && setPrimaryId(f.feedback_id)} disabled={alreadyDup}
                  data-testid={`feedback-merge-pick-${f.feedback_id}`}
                  className={`w-full text-left border px-3 py-2.5 transition-colors ${isP ? "border-olive-bright bg-olive/10" : "border-sniper hover:border-secondary-foreground"} ${alreadyDup ? "opacity-50 cursor-not-allowed" : ""}`}>
                  <div className="flex items-center gap-2">
                    <span className={`font-mono text-[10px] uppercase px-1.5 py-0.5 border ${isP ? "border-olive-bright text-olive-bright" : "border-sniper text-muted-foreground"}`}>{isP ? "Primary" : "Duplicate"}</span>
                    <span className="font-mono text-[10px] uppercase text-muted-foreground">{f.category} · {f.username || f.user_email || "anonymous"} · {timeAgo(f.created_at)}</span>
                    {alreadyDup && <span className="font-mono text-[9px] uppercase text-amber-400">already merged</span>}
                  </div>
                  <p className="text-[12px] font-body text-foreground mt-1 line-clamp-2">{f.message}</p>
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
            <p className="font-mono text-[11px] uppercase text-muted-foreground" data-testid="feedback-merge-summary">
              {[...selected].filter((id) => id !== primaryId).length} ticket(s) will fold into the primary
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setBulk(null)} data-testid="feedback-merge-cancel" className="border border-sniper text-muted-foreground hover:text-foreground font-mono uppercase text-xs px-3 py-2 transition-colors">Cancel</button>
              <button onClick={runMerge} disabled={!primaryId || [...selected].filter((id) => id !== primaryId).length === 0} data-testid="feedback-merge-confirm"
                className="font-mono uppercase text-xs px-4 py-2 border border-olive/60 text-olive-bright hover:bg-olive/10 transition-colors disabled:opacity-50">Merge into primary</button>
            </div>
          </div>
        </BulkShell>
      )}

      {/* ---- single-ticket close / reopen modal ---- */}
      {modal && (() => {
        const isClose = modal.type === "close";
        const busy = busyId === modal.id;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="feedback-modal" onClick={closeModal}>
            <div className="w-full max-w-md border border-sniper bg-surface p-5" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-xl uppercase tracking-tight text-foreground flex items-center gap-2">
                  {isClose ? <><CheckCircle2 className="w-5 h-5 text-green-400" /> Resolve &amp; close</> : <><RotateCcw className="w-5 h-5 text-volt" /> Reopen ticket</>}
                </h3>
                <button onClick={closeModal} data-testid="feedback-modal-close" className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
              </div>

              {isClose ? (
                <div className="mt-4 space-y-3">
                  <p className="text-[12px] font-body text-secondary-foreground">Confirm the verification state before closing. This is recorded in the ticket history.</p>
                  <button type="button" onClick={() => { const nv = !mVerified; setMVerified(nv); if (nv) setMWithout(false); }} data-testid="feedback-verified-check"
                    className={`w-full flex items-center gap-2 border px-3 py-2.5 font-mono text-xs uppercase transition-colors ${mVerified ? "border-green-500/60 text-green-400 bg-green-500/5" : "border-sniper text-secondary-foreground hover:text-foreground"}`}>
                    {mVerified ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />} Verified on PRODUCTION
                  </button>
                  <button type="button" onClick={() => { const nw = !mWithout; setMWithout(nw); if (nw) setMVerified(false); }} data-testid="feedback-without-check"
                    className={`w-full flex items-center gap-2 border px-3 py-2.5 font-mono text-xs uppercase transition-colors ${mWithout ? "border-amber-400/60 text-amber-400 bg-amber-400/5" : "border-sniper text-secondary-foreground hover:text-foreground"}`}>
                    {mWithout ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />} Closing without verification
                  </button>
                  <textarea value={mReason} onChange={(e) => setMReason(e.target.value)} rows={2} placeholder="Optional note…" data-testid="feedback-modal-reason"
                    className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground text-sm resize-none" />
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  <p className="text-[12px] font-body text-secondary-foreground inline-flex items-start gap-1.5"><AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" /> Reopening a closed ticket means a fix isn't holding. A reason is required; reopening ≥2 times flags the ticket.</p>
                  <textarea value={mReason} onChange={(e) => setMReason(e.target.value)} rows={3} autoFocus placeholder="Why are you reopening this? (min 5 characters)" data-testid="feedback-modal-reason"
                    className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground text-sm resize-none" />
                </div>
              )}

              <div className="mt-4 flex items-center justify-end gap-2">
                <button onClick={closeModal} data-testid="feedback-modal-cancel" className="border border-sniper text-muted-foreground hover:text-foreground font-mono uppercase text-xs px-3 py-2 transition-colors">Cancel</button>
                <button onClick={isClose ? submitClose : submitReopen} disabled={busy} data-testid="feedback-modal-submit"
                  className={`font-mono uppercase text-xs px-4 py-2 border transition-colors disabled:opacity-50 ${isClose ? "border-green-500/60 text-green-400 hover:bg-green-500/10" : "border-volt/60 text-volt hover:bg-volt/10"}`}>
                  {busy ? "Working…" : isClose ? "Resolve & close" : "Reopen"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function BulkShell({ title, icon, onClose, wide, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="feedback-bulk-modal" onClick={onClose}>
      <div className={`w-full ${wide ? "max-w-xl" : "max-w-md"} border border-sniper bg-surface p-5`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-xl uppercase tracking-tight text-foreground flex items-center gap-2">{icon} {title}</h3>
          <button onClick={onClose} data-testid="feedback-bulk-modal-close" className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}
